import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from '../../../i18n';
import {
  getNanoReferralPetAgeCookie,
  getNanoReferralPetBreedCookie,
  getNanoReferralPetNameCookie,
  getNanoReferralPetGenderCookie,
  getNanoReferralPetWeightCookie,
  setNanoReferralPetAgeCookie,
  setNanoReferralPetBreedCookie,
  setNanoReferralPetNameCookie,
  setNanoReferralPetGenderCookie,
  setNanoReferralPetWeightCookie,
} from '../../lib/util/nanoReferralPetNameCookie';
import styles from './NanoBanner.module.scss';
import { DateTime } from 'luxon';
import { useLocation } from 'react-router-dom';
import { StoreCart } from '../../types';
import {
  inCartMiniCollarCount,
  inCartPrepaidMembershipCount,
  inCartS3CollarCount,
} from '../ItemsInCart/CouponLineItemV2';
import { getFiGrowthBook } from '../../lib/growthbook';
import classNames from 'classnames';
import { Species } from '../../types/gql-op-types';
import { PromotionBanner } from '@barkinglabs/promotions-react';
import { PromotionConfigurationKind } from '@barkinglabs/promotions-types';

export function getNanoDeviceOfferEnabled() {
  const elevatedNanoOffer = getFiGrowthBook().getFeatureValue<{ mini: boolean; s3p: boolean }>(
    'nano-device-offer-enabled',
    { mini: false, s3p: false },
  );

  if (window.location.pathname.includes('smart-collar') && !elevatedNanoOffer.s3p) {
    return false;
  }

  if (window.location.pathname.includes('mini') && !elevatedNanoOffer.mini) {
    return false;
  }

  return true;
}

export function showNanoVariant() {
  if (!getNanoDeviceOfferEnabled()) {
    return false;
  }

  const petName = getNanoVariantPetName();
  const petAge = getNanoVariantPetAgeMonths();
  const petGender = getNanoVariantPetGender();
  const petWeight = getNanoVariantPetWeight();
  const petBreed = getNanoVariantPetBreed();

  if (!!petName && petAge >= 0 && !!petGender && !!petWeight && !!petBreed) {
    setNanoReferralPetNameCookie(petName);
    setNanoReferralPetAgeCookie(petAge.toString());
    setNanoReferralPetGenderCookie(petGender);
    setNanoReferralPetWeightCookie(petWeight);
    setNanoReferralPetBreedCookie(petBreed);
    return true;
  }

  return false;
}

export function getNanoVariantPetName(species: Species = Species.DOG) {
  let petName = new URLSearchParams(window.location.search).get('pet_name');
  if (!petName || petName.length > 10) {
    petName =
      species === Species.DOG
        ? i18next.t('your_dog')
        : species === Species.CAT
        ? i18next.t('your_cat')
        : i18next.t('your_pet');
  }
  return petName || getNanoReferralPetNameCookie();
}

export function getNanoVariantPetAgeMonths() {
  return Number(new URLSearchParams(window.location.search).get('pet_age') || getNanoReferralPetAgeCookie());
}

export function getNanoVariantPetGender() {
  return new URLSearchParams(window.location.search).get('pet_gender') || getNanoReferralPetGenderCookie();
}

export function getNanoVariantPetBreed() {
  return new URLSearchParams(window.location.search).get('pet_breed') || getNanoReferralPetBreedCookie();
}

export function getNanoVariantPetWeight() {
  return new URLSearchParams(window.location.search).get('pet_weight') || getNanoReferralPetWeightCookie();
}

const CountdownTimer = () => {
  const { t } = useTranslation();
  const calculateTimeLeft = () => {
    const now = DateTime.local();
    let targetTime = now.set({ hour: 9, minute: 0, second: 0, millisecond: 0 });

    // If the current time is already past 9:00 AM today, set the target time to 9:00 AM tomorrow
    if (now >= targetTime) {
      targetTime = targetTime.plus({ days: 1 });
    }

    return targetTime.diff(now, ['hours', 'minutes', 'seconds']);
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const hours = Math.floor(timeLeft.hours).toString().padStart(2, '0');
  const minutes = Math.floor(timeLeft.minutes).toString().padStart(2, '0');
  const seconds = Math.floor(timeLeft.seconds).toString().padStart(2, '0');

  return (
    <div className={styles.countdown}>
      <div className={styles.expiresSoonDesktop}>
        <div>{t('expires')}</div>
        <div>{t('soon')}</div>
      </div>
      <div className={styles.expiresSoonMobile}>
        <div>{t('expires_soon')}</div>
      </div>
      <div className={styles.countdownClock}>
        <div className={styles.hours}>{hours}</div>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        <div className={styles.timeSeparator}>:</div>
        <div className={styles.minutes}>{minutes}</div>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        <div className={styles.timeSeparator}>:</div>
        <div className={styles.seconds}>{seconds}</div>
      </div>
    </div>
  );
};

const useAutoApplyNanoCouponOnCollarAdded = (cart: StoreCart, promoCodeApplied: boolean, onClick?: () => void) => {
  const cartItems = Object.values(cart.cartItems);

  const { pathname } = useLocation();

  useEffect(() => {
    const totalCollarCount = inCartS3CollarCount(cartItems) + inCartMiniCollarCount(cartItems);
    const monthToMonthCollarCount = inCartPrepaidMembershipCount(cartItems);
    // From the cart, we want to auto-apply the promo code by simulating a click event on the banner.
    if (
      !promoCodeApplied &&
      pathname.startsWith('/bag') &&
      totalCollarCount > 0 &&
      totalCollarCount - monthToMonthCollarCount > 0
    ) {
      onClick && onClick();
    }
    // eslint-disable-next-line
  }, [pathname]);
};

export function NanoBanner({
  cart,
  onClick,
  promoCodeApplied,
}: {
  cart: StoreCart;
  onClick?: () => void;
  promoCodeApplied: boolean;
}) {
  const { t } = useTranslation();
  const elevatedNanoOffer = getFiGrowthBook().getFeatureValue<boolean | null>('nano-elevated-promo-offer', false);

  useAutoApplyNanoCouponOnCollarAdded(cart, promoCodeApplied, onClick);

  const discountAmount = elevatedNanoOffer ? 99 : 20;
  const couponCode = elevatedNanoOffer ? 'nano99' : 'nano20';

  const discountText = elevatedNanoOffer
    ? t('dollar_amount_off_all_pre_paid_plans_auto_applied_at_checkout', { discountAmount })
    : t('dollar_amount_off_with_code_coupon_code', { discountAmount, couponCode });

  return (
    <button className={classNames(styles.nanoBanner, 'nanoBanner')} onClick={onClick}>
      <div className={styles.textContainer}>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        {!promoCodeApplied && <div className={styles.image}>🐶</div>}
        <div className={styles.titleContainer}>
          <div className={styles.title}>
            {promoCodeApplied ? t('offer_applied_to_cart') : t('exclusive_fi_nano_offer')}
          </div>
          {!promoCodeApplied && <div className={styles.subtitle}>{discountText}</div>}
        </div>
      </div>
      <CountdownTimer />
    </button>
  );
}

export function NanoBannerV2({
  cart,
  onClick,
  promoCodeApplied,
}: {
  cart: StoreCart;
  onClick?: () => void;
  promoCodeApplied: boolean;
}) {
  const { t } = useTranslation();
  useAutoApplyNanoCouponOnCollarAdded(cart, promoCodeApplied, onClick);

  return (
    <PromotionBanner
      className={'promotionBanner'}
      promotion={{
        configuration: {
          kind: PromotionConfigurationKind.SIMPLE,
          title: '',
          subtitle: t('dollar_amount_off_will_be_automatically_applied_in_your_cart', { amount: 99 }),
          couponCode: 'NANO99',
          countdown: 'static',
          extraCss: undefined,
          imageUrl: undefined,
          backgroundImageUrl: undefined,
        },
      }}
      promotionEnd={t('offer_ends_today')}
    />
  );
}
